import { deployEnvironment, useClients } from '@features/clients';
import { Badge, ErrorState, LoadingRows } from '@shared/ui';
import { Link } from 'react-router';

import { useReleaseClients } from '../hooks/use-release-components';

import { clientsEnvironmentView } from './clients-environment';
import { releaseClientsView } from './release-clients';

/**
 * The clients a release is on for now, derived by the API from flag state rather
 * than from what an operator once selected. Names are joined from the clients
 * list of the deploy environment; if that read fails the rows show tenant ids.
 */
export function ReleaseClientsSection({
    releaseId,
    flagCount,
}: {
    releaseId: string;
    flagCount: number;
}) {
    const applied = useReleaseClients(releaseId);
    const clients = useClients(clientsEnvironmentView(deployEnvironment()).target);
    const heading = (
        <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-content-muted">
            Clients
        </h4>
    );

    if (applied.isPending) {
        return (
            <section aria-label="Clients">
                {heading}
                <LoadingRows label="Loading clients…" />
            </section>
        );
    }

    if (applied.isError) {
        return (
            <section aria-label="Clients">
                {heading}
                <ErrorState error={applied.error} onRetry={() => void applied.refetch()} />
            </section>
        );
    }

    const view = releaseClientsView(applied.data, clients.data, flagCount);

    return (
        <section aria-label="Clients">
            {heading}
            {view.kind === 'all' ? (
                <div className="flex flex-col gap-1">
                    <p className="text-[13.5px] font-medium text-content-strong">{view.label}</p>
                    <p className="text-xs text-content-muted">{view.description}</p>
                </div>
            ) : (
                <div className="flex flex-col gap-2">
                    {view.empty !== undefined ? (
                        <span className="text-xs text-content-faint">{view.empty}</span>
                    ) : (
                        <ul className="flex flex-col text-[13px]">
                            {view.rows.map((row) => (
                                <li
                                    key={row.tenantId}
                                    className="flex flex-wrap items-center gap-2 border-t border-line-subtle py-2"
                                >
                                    <Link
                                        to={row.href}
                                        className="font-medium text-accent-text hover:underline"
                                    >
                                        {row.name}
                                    </Link>
                                    <Badge tone={row.complete ? 'success' : 'neutral'}>
                                        {row.coverageLabel}
                                    </Badge>
                                    <span className="text-xs text-content-muted">{row.detail}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {view.note !== undefined && (
                        <p className="text-xs text-content-muted">{view.note}</p>
                    )}
                </div>
            )}
        </section>
    );
}
